/**
 * DriveToolCard — carte Google Drive dans le Bureau cockpit.
 *
 * Batch #14.31 — Si Drive auto-créé OK → ouvre le dossier. Sinon → inline form
 * pour coller l'URL Drive manuellement. Permet à l'admin de gérer le Drive
 * lui-même (plus simple, plus safe).
 *
 * Extrait de Bureau.jsx (Batch #14.221).
 */
import { useState } from "react";
import { FolderOpen, ExternalLink, Link2 } from "lucide-react";
import DriveLinkForm from "./DriveLinkForm";

export default function DriveToolCard({ hub, masterId, expertise, onLinked }) {
  const drive = hub?.drive || {};
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState("");

  // Cas 1 : Drive déjà configuré → carte standard avec bouton "Ouvrir"
  if (drive.folder_id) {
    return (
      <div className="border border-indigo-200 hover:border-indigo-400 rounded-xl p-4 bg-white hover:shadow-md min-h-[140px] transition" data-testid="bureau-drive">
        <div className="flex items-start justify-between">
          <FolderOpen className="w-6 h-6 text-indigo-600" />
          <button
            onClick={() => { setEditing(true); setUrl(drive.folder_url || ""); }}
            className="text-[10px] text-slate-400 hover:text-indigo-600 inline-flex items-center gap-0.5"
            title="Re-lier vers un autre dossier"
            data-testid="bureau-drive-edit-btn"
          >
            <Link2 className="w-3 h-3" /> Modifier
          </button>
        </div>
        <div className="font-semibold text-sm text-slate-800 mt-3">Dossier Drive</div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {drive.folder_linked_manually ? "Lié manuellement" : "Auto-créé"}
        </div>
        <a
          href={drive.folder_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-indigo-600 hover:text-indigo-800"
          data-testid="bureau-drive-open-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le Drive
        </a>
        {editing && (
          <DriveLinkForm
            masterId={masterId}
            expertise={expertise}
            initialUrl={url}
            onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); onLinked?.(); }}
          />
        )}
      </div>
    );
  }

  // Cas 2 : Drive non configuré → form pour le lier manuellement
  return (
    <div
      className="border border-dashed border-indigo-300 rounded-xl p-4 bg-indigo-50/30 min-h-[140px]"
      data-testid="bureau-drive-empty"
    >
      <FolderOpen className="w-6 h-6 text-indigo-400" />
      <div className="font-semibold text-sm text-slate-700 mt-3">Dossier Drive</div>
      <div className="text-[11px] text-slate-500 mt-0.5">
        Non configuré — créez le dossier dans votre Drive et collez le lien ici
      </div>
      {!editing ? (
        <button
          onClick={() => setEditing(true)}
          className="mt-3 px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white text-xs inline-flex items-center gap-1.5 hover:bg-indigo-700 disabled:opacity-40"
          data-testid="bureau-drive-link-btn"
        >
          <Link2 className="w-3.5 h-3.5" /> Lier un dossier Drive
        </button>
      ) : (
        <DriveLinkForm
          masterId={masterId}
          expertise={expertise}
          initialUrl=""
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onLinked?.(); }}
        />
      )}
    </div>
  );
}
